import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Mic, MicOff, PhoneOff, Volume2, VolumeX } from 'lucide-react';
import { SlideToAnswer } from './SlideToAnswer';
import { CallAvatar } from './CallAvatar';

const formatCallDuration = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// Palette — mirror of IncomingCallActivity.java (white theme, 2026 refresh).
const BG_WHITE = '#FFFFFF';
const TEXT_PRIMARY = '#0F0A1F';
const TEXT_SECONDARY = '#5B5B70';
const SURFACE_SOFT = '#F3F0F8';
const MUTE_BG = '#2A1A3E';
const MUTE_BG_ACTIVE = '#E94C57';
const SPEAKER_BG = '#2A1A3E';
const SPEAKER_BG_ACTIVE = '#6E4BC9';
const END_BG = '#E94C57';

/**
 * React mirror of the native IncomingCallActivity (SDK 24+) with the
 * same white-theme layout: wordmark top → avatar (with pulsing dark
 * drop shadow) → name → status, spacer, then the slide-to-answer
 * widget on ringing OR the mute / speaker / end row on connected.
 * The Allure colour triangle sits as a top-overlay and flies in from
 * the upper-right on connect, then bobs gently.
 *
 * Any delta vs. `IncomingCallActivity.java` here is a layout bug.
 */
export const MessengerCallOverlay = ({
  open,
  onMinimize,
  canMinimize = true,
  onEnd,
  onAccept,
  onDecline,
  onToggleMute,
  onToggleSpeaker,
  contactName,
  contactImage,
  dataTestId,
  status = 'connecting',
  durationSeconds = 0,
  isMuted = false,
  speakerOn = true,
  socketConnected = true,
}) => {
  const isRinging = status === 'incoming' || status === 'calling';
  const isConnected = status === 'active';
  const statusCopy = status === 'incoming'
    ? 'Incoming voice call…'
    : status === 'calling'
      ? 'Calling…'
      : status === 'reconnecting'
        ? 'Reconnecting…'
        : status === 'active'
          ? `Connected · ${formatCallDuration(durationSeconds)}`
          : 'Connecting…';

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="call-overlay"
          data-testid={dataTestId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 13000,
            overflow: 'hidden',
            background: BG_WHITE,
            color: TEXT_PRIMARY,
            fontFamily: "'Inria Serif', serif",
          }}
        >
          {/* Top-overlay Allure triangle logo (flies in on connect, then bobs) */}
          <AnimatePresence>
            {isConnected ? (
              <motion.img
                key="allure-top-logo"
                src="/allure_logo_transparent.png"
                alt=""
                aria-hidden="true"
                initial={{ x: 180, y: -220, scale: 1.4, opacity: 0 }}
                animate={{
                  x: 0,
                  y: [0, -4, 0],
                  scale: 1,
                  opacity: 1,
                }}
                transition={{
                  x: { duration: 0.42, ease: [0.17, 0.84, 0.44, 1] },
                  scale: { duration: 0.42, ease: [0.17, 0.84, 0.44, 1] },
                  opacity: { duration: 0.42, ease: [0.17, 0.84, 0.44, 1] },
                  y: { duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 0.42 },
                }}
                style={{
                  position: 'absolute',
                  top: 'calc(env(safe-area-inset-top, 0px) + 72px)',
                  left: '50%',
                  marginLeft: -36,
                  width: 72,
                  height: 72,
                  objectFit: 'contain',
                  zIndex: 2,
                }}
              />
            ) : null}
          </AnimatePresence>

          {/* Top controls (minimize + connection dot) — kept inside the frame
              but positioned so they don't steal space from the column */}
          <div
            style={{
              position: 'absolute',
              top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
              left: 20,
              right: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 3,
            }}
          >
            {canMinimize ? (
              <button
                type="button"
                onClick={onMinimize}
                data-testid={`${dataTestId}-collapse-button`}
                aria-label="Minimize call"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  border: '1px solid rgba(15, 10, 31, 0.08)',
                  background: SURFACE_SOFT,
                  color: TEXT_PRIMARY,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <ChevronDown size={22} />
              </button>
            ) : <span style={{ width: 40, height: 40 }} />}
            <div
              data-testid={`${dataTestId}-connection-dot`}
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: socketConnected ? '#35D178' : 'rgba(15, 10, 31, 0.25)',
                boxShadow: socketConnected ? '0 0 10px rgba(53, 209, 120, 0.55)' : 'none',
              }}
            />
          </div>

          {/* Column — mirrors the LinearLayout(VERTICAL) in Java */}
          <div
            style={{
              position: 'relative',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 'calc(env(safe-area-inset-top, 0px) + 128px) 24px calc(env(safe-area-inset-bottom, 0px) + 36px)',
            }}
          >
            {/* Wordmark — serif bold 22sp, dark */}
            <h1
              data-testid={`${dataTestId}-wordmark`}
              style={{
                margin: 0,
                fontFamily: "'Inria Serif', serif",
                fontWeight: 700,
                fontSize: 22,
                color: TEXT_PRIMARY,
                letterSpacing: '-0.01em',
                textAlign: 'center',
              }}
            >
              Allure Experience
            </h1>

            {/* V1: Avatar keeps a steady 148dp through-and-through (no
                connect-scale) so the swap to connected UI doesn't visually
                collapse the column. Pulsing shadow handled inside CallAvatar. */}
            <div style={{ marginTop: 20, position: 'relative' }}>
              <CallAvatar
                name={contactName}
                imageUrl={contactImage}
                size={148}
                ringing={isRinging}
                dataTestId={`${dataTestId}-avatar`}
              />
            </div>

            {/* Name — serif bold 30sp */}
            <h2
              data-testid={`${dataTestId}-name`}
              style={{
                margin: '22px 0 0',
                fontFamily: "'Inria Serif', serif",
                fontSize: 30,
                fontWeight: 700,
                color: TEXT_PRIMARY,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                textAlign: 'center',
                maxWidth: 320,
              }}
            >
              {contactName}
            </h2>

            {/* Ringing status — serif 16sp, hidden on connect */}
            {!isConnected ? (
              <motion.p
                data-testid={`${dataTestId}-status`}
                animate={
                  isRinging
                    ? { opacity: [0.6, 1, 0.6] }
                    : { opacity: 0.85 }
                }
                transition={
                  isRinging
                    ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }
                    : { duration: 0.3 }
                }
                style={{
                  margin: '6px 0 0',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 16,
                  color: TEXT_SECONDARY,
                  letterSpacing: '0.01em',
                  textAlign: 'center',
                }}
              >
                {statusCopy}
              </motion.p>
            ) : (
              <p
                data-testid={`${dataTestId}-status`}
                style={{
                  margin: '6px 0 0',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 16,
                  color: TEXT_SECONDARY,
                  textAlign: 'center',
                }}
              >
                {statusCopy}
              </p>
            )}

            {/* Flexible spacer — matches the weight-1 spacer in Java */}
            <div style={{ flex: 1 }} />

            {/* Control block — slide-to-answer (ringing) OR mute/speaker/end
                (calling / connecting / active). A2: outgoing-calling now gets
                the same Mute/Speaker/End trio as an active call so the caller
                can route the ringback through the earpiece or mute their mic
                before the callee answers. */}
            {status === 'incoming' ? (
              <div style={{ width: '100%' }}>
                <SlideToAnswer
                  dataTestId={`${dataTestId}-slide-to-answer`}
                  onAnswer={onAccept}
                  onDecline={onDecline}
                />
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 28,
                  marginTop: 24,
                }}
              >
                <PillIcon
                  dataTestId={`${dataTestId}-mute-button`}
                  onClick={onToggleMute}
                  background={isMuted ? MUTE_BG_ACTIVE : MUTE_BG}
                  icon={isMuted ? <MicOff size={22} /> : <Mic size={22} />}
                  label={isMuted ? 'Unmute' : 'Mute'}
                />
                <PillIcon
                  dataTestId={`${dataTestId}-speaker-button`}
                  onClick={onToggleSpeaker}
                  background={speakerOn ? SPEAKER_BG_ACTIVE : SPEAKER_BG}
                  icon={speakerOn ? <Volume2 size={22} /> : <VolumeX size={22} />}
                  label={speakerOn ? 'Speaker' : 'Earpiece'}
                />
                <PillIcon
                  dataTestId={`${dataTestId}-end-button`}
                  onClick={onEnd}
                  background={END_BG}
                  icon={<PhoneOff size={22} />}
                  label="End"
                />
              </div>
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

/** Mirror of PillIcon (Java inner class) — 72x96 column with 56dp disc + 13sp label. */
const PillIcon = ({ dataTestId, onClick, background, icon, label }) => (
  <button
    type="button"
    data-testid={dataTestId}
    onClick={onClick}
    style={{
      width: 72,
      height: 96,
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      color: TEXT_SECONDARY,
    }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
        transition: 'background 220ms ease',
      }}
    >
      {icon}
    </div>
    <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: TEXT_SECONDARY }}>{label}</span>
  </button>
);
